/**
 * Superadmin view of person entities, plus claim-link generation/revocation
 * (backend: POST/DELETE /api/entities/{id}/claim-link).
 *
 * One fetch, two slices: `allPeople` is every person on the node (listed or
 * not), `people` narrows that to the unclaimed ones (user_id=null). The plain
 * /api/entities?listed=true browse fetch never returns unlisted rows, so this
 * is how a superadmin sees the people that listing hides.
 *
 * GET /api/entities isn't superadmin-gated (it's the general listing endpoint),
 * so unlike use_super_admins/use_pending_registrations we can't use a 403 as the
 * "am I the server admin" signal — gate on useSuperAdmins() instead and only
 * fetch once that resolves true.
 *
 *   const { people, allPeople, isServerAdmin, loading, createClaimLink, revokeClaimLink } = useUnclaimedPeople();
 */

import { useState, useEffect, useCallback } from 'react';
import { useApi } from './use_api';
import { useAuth } from '../context/AuthContext';
import { useSuperAdmins } from './use_super_admins';

export function useUnclaimedPeople() {
    const api = useApi();
    const { user } = useAuth();
    const userId = user?.id;
    const { isServerAdmin, loading: superAdminsLoading } = useSuperAdmins();

    const [people, setPeople] = useState([]);
    // Every person entity, listed or not — same fetch, just not narrowed to the
    // unclaimed ones. /api/entities is not superadmin-gated, but this hook only
    // ever runs for a superadmin (see the gate in load below).
    const [allPeople, setAllPeople] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        if (superAdminsLoading) return;
        if (!isServerAdmin) { setLoading(false); return; }
        setLoading(true);
        api.get('/api/entities', { params: { entity_type: 'person' } })
            .then(data => {
                const rows = Array.isArray(data) ? data : [];
                setAllPeople(rows);
                setPeople(rows.filter(e => !e.user_id));
            })
            .catch(() => { setAllPeople([]); setPeople([]); })
            .finally(() => setLoading(false));
    }, [api, isServerAdmin, superAdminsLoading]);

    useEffect(() => { load(); }, [load]);

    // sendEmail=false mints/returns the same link without emailing it, so a
    // person with no email on file can still be invited by sharing the URL.
    const createClaimLink = useCallback((entityId, sendEmail = true) =>
        api.post(`/api/entities/${entityId}/claim-link`, undefined, {
            params: { user_id: userId, send_email: sendEmail },
        })
    , [api, userId]);

    const revokeClaimLink = useCallback(entityId =>
        api.del(`/api/entities/${entityId}/claim-link`, { params: { user_id: userId } }).then(() => load())
    , [api, userId, load]);

    return {
        people,
        allPeople,
        isServerAdmin,
        loading,
        refresh: load,
        createClaimLink,
        revokeClaimLink,
    };
}

export default useUnclaimedPeople;
